/**
 * hotbarSegmentSwatch — the CSS `background` for a Segment's swatch in a Hotbar
 * tab header (ImagesTab/VideosTab/... -> IconContainer bgColor).
 *
 * T22.180: the swatch used to be the Segment's base `settings.color` only. Once
 * a Gradient (or Pattern/Image/GIF) Segment Fill is picked, the wedge paints
 * that fill (createPie, via hydrateChartFill -> d.data.fill) while
 * `settings.color` keeps the OLD solid colour. Measured live on web-stg
 * (wheel 507651 + a copy of 507029): the filled Segment's wedge turned pink but
 * its Hotbar header kept a steel-blue swatch, so its images read as belonging to
 * "the Blue one" - a Segment that no longer looks blue on the Wheel.
 *
 * Resolve the swatch from the SAME fill model the wedge paints from
 * (resolveSegmentFill + buildSegmentFillCss), behind the SAME flag
 * (REACT_APP_SEGMENT_ARC_FILLS). No fill / flag OFF -> `settings.color`,
 * exactly as before.
 */
import { SEGMENT_ARC_FILLS_ENABLED } from "../D3Wheels/segmentArcFillsFlag";
import { resolveSegmentFill } from "../SegmentBuilder/Shared/SegmentStyling/logic/fillModelFeed";
import { buildSegmentFillCss } from "../SegmentBuilder/Shared/SegmentStyling/logic/segmentFill";

export const hotbarSegmentSwatch = (segment, enabled = SEGMENT_ARC_FILLS_ENABLED) => {
  const color = segment?.settings?.color;
  if (!enabled || !segment) return color;
  const fill = resolveSegmentFill(segment);
  if (!fill) return color;
  return buildSegmentFillCss(fill) || color;
};

/**
 * T22.292: what the wheel paints for a Segment with no saved colour (the v1
 * engine's empty wedge, createPie with the outline flag OFF), so its Hotbar
 * swatch and tint are never blank.
 */
export const HOTBAR_EMPTY_SWATCH = "#E1E1E1";

const firstHex = (css) => {
  const m = typeof css === "string" && css.match(/#([0-9a-f]{6}|[0-9a-f]{3})\b/i);
  if (!m) return null;
  const h = m[1];
  return h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
};

/**
 * T22.292: the translucent group tint behind a Hotbar tab's rows. The old
 * `hexToRgba(segmentColor)` threw on a null colour ("Cannot read properties of
 * null (reading 'replace')" -> the whole Edit Wheel fell to the error screen
 * when the Links tab opened) and gave rgba(NaN..) for a gradient swatch. Use
 * the swatch's first hex (a gradient's first stop), else the empty-wedge grey.
 */
export const hotbarSegmentTint = (segmentColor, alpha) => {
  const h = firstHex(segmentColor) || firstHex(HOTBAR_EMPTY_SWATCH);
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default hotbarSegmentSwatch;
